/* ============================================================
   AppSupertools — Supabase Auth + Toolkit Sync
   ast-auth.js
   ============================================================ */

(function () {
  'use strict';

  var SUPABASE_URL = 'https://tlqvenjoosdjrywsoebj.supabase.co';
  var SUPABASE_ANON_KEY = 'sb_publishable_1rKVuR_HpY17z9dbF0kQlw_clSufNHa';
  var REDIRECT_URL = 'https://appsupertools.com/pages/my-toolkit';

  /* All keys that make up the toolkit state */
  var TOOLKIT_KEYS = ['ast_saved_tools', 'ast_toolkit_order', 'ast_projects'];

  /* ── Supabase client ──────────────────────────────────────── */
  function getClient() {
    if (window._astSupabase) return window._astSupabase;
    if (!window.supabase) { console.warn('[AST] Supabase SDK not loaded'); return null; }
    window._astSupabase = window.supabase.createClient(SUPABASE_URL, SUPABASE_ANON_KEY);
    return window._astSupabase;
  }

  /* ── Auth helpers ─────────────────────────────────────────── */
  function getSession(cb) {
    var client = getClient();
    if (!client) return cb(null);
    client.auth.getSession().then(function (res) {
      cb(res.data && res.data.session ? res.data.session : null);
    });
  }

  function signInWithGoogle() {
    var client = getClient();
    if (!client) return;
    client.auth.signInWithOAuth({
      provider: 'google',
      options: { redirectTo: REDIRECT_URL }
    });
  }

  function signInWithEmail(email, password, cb) {
    var client = getClient();
    if (!client) return;
    client.auth.signInWithPassword({ email: email, password: password })
      .then(function (res) { cb(res.error, res.data); });
  }

  function signUpWithEmail(email, password, cb) {
    var client = getClient();
    if (!client) return;
    client.auth.signUp({
      email: email,
      password: password,
      options: { emailRedirectTo: REDIRECT_URL }
    }).then(function (res) { cb(res.error, res.data); });
  }

  function signOut(cb) {
    var client = getClient();
    if (!client) return;
    client.auth.signOut().then(function () { if (cb) cb(); });
  }

  /* ── Bundle all toolkit localStorage keys into one object ─── */
  function bundleLocalData() {
    var bundle = {};
    TOOLKIT_KEYS.forEach(function (key) {
      try {
        var raw = localStorage.getItem(key);
        if (raw) bundle[key] = JSON.parse(raw);
      } catch (e) {}
    });
    return bundle;
  }

  function applyBundleToLocal(bundle) {
    TOOLKIT_KEYS.forEach(function (key) {
      try {
        if (bundle[key] !== undefined) {
          localStorage.setItem(key, JSON.stringify(bundle[key]));
        }
      } catch (e) {}
    });
  }

  function bundleHasData(bundle) {
    if (!bundle) return false;
    return TOOLKIT_KEYS.some(function (key) {
      var v = bundle[key];
      if (!v) return false;
      if (Array.isArray(v)) return v.length > 0;
      if (typeof v === 'object') return Object.keys(v).length > 0;
      return false;
    });
  }

  /* ── Supabase data helpers ────────────────────────────────── */
  function loadFromSupabase(userId, cb) {
    var client = getClient();
    if (!client) return cb(null);
    client.from('toolkit_data')
      .select('data')
      .eq('user_id', userId)
      .maybeSingle()
      .then(function (res) {
        if (res.error || !res.data) return cb(null);
        cb(res.data.data);
      });
  }

  function saveToSupabase(userId, bundle, cb) {
    var client = getClient();
    if (!client) return;
    client.from('toolkit_data')
      .upsert({ user_id: userId, data: bundle, updated_at: new Date().toISOString() }, { onConflict: 'user_id' })
      .then(function (res) { if (cb) cb(res.error); });
  }

  /* ── Sync on login: remote wins, else push local up ──────── */
  function syncOnLogin(userId, cb) {
    var localBundle = bundleLocalData();
    var localSnapshot = JSON.stringify(localBundle);
    loadFromSupabase(userId, function (remoteBundle) {
      if (bundleHasData(remoteBundle)) {
        /* Remote has data — apply it to localStorage and reload UI only if different */
        var remoteSnapshot = JSON.stringify(remoteBundle);
        applyBundleToLocal(remoteBundle);
        if (cb) cb(remoteSnapshot !== localSnapshot ? 'remote' : 'same');
      } else if (bundleHasData(localBundle)) {
        /* No remote data yet — push local up */
        saveToSupabase(userId, localBundle, function () {
          if (cb) cb('local');
        });
      } else {
        if (cb) cb('empty');
      }
    });
  }

  /* ── Auto-save with smart event differentiation ─────────── */
  var _saveTimer = null;
  var _hasPendingMinor = false;

  function initAutoSave(userId) {
    /* Important actions (add/remove tool, create project, save workspace) → debounced 3s */
    window.addEventListener('ast:toolkitsaved-important', function () {
      clearTimeout(_saveTimer);
      _hasPendingMinor = false;
      _saveTimer = setTimeout(function () {
        saveToSupabase(userId, bundleLocalData());
        _saveTimer = null;
      }, 3000);
    });

    /* Minor actions (reorder) → flag only, save on unload */
    window.addEventListener('ast:toolkitsaved-minor', function () {
      if (!_saveTimer) _hasPendingMinor = true;
    });

    /* Always save on tab close — catches both pending timers and minor flags */
    window.addEventListener('beforeunload', function () {
      if (_saveTimer || _hasPendingMinor) {
        clearTimeout(_saveTimer);
        saveToSupabase(userId, bundleLocalData());
      }
    });
  }

  /* ── Auth state change listener ───────────────────────────── */
  function initAuthListener() {
    var client = getClient();
    if (!client) return;
    client.auth.onAuthStateChange(function (event, session) {
      window.dispatchEvent(new CustomEvent('ast:authchange', {
        detail: { event: event, session: session, user: session ? session.user : null }
      }));
    });
  }

  /* ── Toolkit page sync init ───────────────────────────────── */
  function initToolkitSync() {
    initAuthListener();
    getSession(function (session) {
      if (!session) return;
      var userId = session.user.id;
      syncOnLogin(userId, function (source) {
        initAutoSave(userId);
        if (source === 'remote') {
          /* Data loaded from remote — render UI now with correct data */
          setTimeout(function () {
            if (typeof window._atkEarlyInit === 'function') {
              window._atkEarlyInit();
            } else if (typeof window.atkReloadFromStorage === 'function') {
              window.atkReloadFromStorage();
            }
          }, 50);
        } else {
          /* Local or empty — just trigger the early init so render happens */
          setTimeout(function () {
            if (typeof window._atkEarlyInit === 'function') {
              window._atkEarlyInit();
            }
          }, 50);
        }
      });
    });
  }

  /* ── Login page UI ────────────────────────────────────────── */
  function initLoginPage() {
    var form = document.getElementById('ast-login-form');
    if (!form) return;

    var emailInput = document.getElementById('ast-email');
    var passwordInput = document.getElementById('ast-password');
    var submitBtn = document.getElementById('ast-submit-btn');
    var googleBtn = document.getElementById('ast-google-btn');
    var toggleLink = document.getElementById('ast-toggle-mode');
    var modeLabel = document.getElementById('ast-mode-label');
    var errorEl = document.getElementById('ast-error');
    var successEl = document.getElementById('ast-success');
    var isSignUp = false;

    getSession(function (session) {
      if (session) window.location.href = REDIRECT_URL;
    });

    if (googleBtn) {
      googleBtn.addEventListener('click', function () {
        googleBtn.disabled = true;
        googleBtn.textContent = 'Redirecting…';
        signInWithGoogle();
      });
    }

    if (toggleLink) {
      toggleLink.addEventListener('click', function (e) {
        e.preventDefault();
        isSignUp = !isSignUp;
        modeLabel.textContent = isSignUp ? 'Create your account' : 'Sign in to your account';
        document.getElementById('ast-submit-label').textContent = isSignUp ? 'Create account' : 'Sign in';
        toggleLink.textContent = isSignUp ? 'Already have an account? Sign in' : "Don't have an account? Sign up";
        errorEl.textContent = '';
        successEl.textContent = '';
        successEl.style.display = 'none';
      });
    }

    form.addEventListener('submit', function (e) {
      e.preventDefault();
      var email = emailInput.value.trim();
      var password = passwordInput.value;
      if (!email || !password) { errorEl.textContent = 'Please enter your email and password.'; return; }
      submitBtn.disabled = true;
      submitBtn.querySelector('#ast-submit-label').textContent = isSignUp ? 'Creating account…' : 'Signing in…';
      errorEl.textContent = '';

      if (isSignUp) {
        signUpWithEmail(email, password, function (err) {
          submitBtn.disabled = false;
          submitBtn.querySelector('#ast-submit-label').textContent = 'Create account';
          if (err) { errorEl.textContent = err.message; return; }
          successEl.textContent = 'Check your email to confirm your account, then sign in.';
          successEl.style.display = 'block';
        });
      } else {
        signInWithEmail(email, password, function (err) {
          submitBtn.disabled = false;
          submitBtn.querySelector('#ast-submit-label').textContent = 'Sign in';
          if (err) { errorEl.textContent = err.message; return; }
          window.location.href = REDIRECT_URL;
        });
      }
    });
  }

  /* ── Public API ───────────────────────────────────────────── */
  window.ASTAuth = {
    getSession: getSession,
    signInWithGoogle: signInWithGoogle,
    signInWithEmail: signInWithEmail,
    signUpWithEmail: signUpWithEmail,
    signOut: signOut,
    saveToSupabase: saveToSupabase,
    loadFromSupabase: loadFromSupabase,
    syncOnLogin: syncOnLogin,
    bundleLocalData: bundleLocalData
  };

  /* ── Auto-init ────────────────────────────────────────────── */
  document.addEventListener('DOMContentLoaded', function () {
    if (document.getElementById('ast-login-form')) initLoginPage();
    if (document.getElementById('ast-sync-bar')) initToolkitSync();
  });

})();
